// Feature access configuration based on subscription tiers
// Basic: 3 subjects, basic quizzes, parent dashboard, chapter essence, progress tracking
// Pro: All subjects, unlimited quizzes, AI video explainers, mock exams, group study
// Family: Everything in Pro + multi-child, multi-parent

export const SUBSCRIPTION_FEATURES = {
  free: {
    subjects: 0,
    quizzesPerDay: 0,
    features: [],
  },
  basic: {
    subjects: 3,
    quizzesPerDay: 5,
    features: [
      'chapter_essence',
      'basic_quiz',
      'parent_dashboard',
      'progress_tracking',
      'exam_calendar',
    ],
  },
  pro: {
    subjects: 9, // All subjects including Physics, Chemistry, Biology
    quizzesPerDay: -1, // Unlimited
    features: [
      'chapter_essence',
      'basic_quiz',
      'parent_dashboard',
      'progress_tracking',
      'exam_calendar',
      'ai_video_explainer',
      'mock_exam',
      'group_study',
      'ai_chat',
      'ocr_upload',
      'audio_narration',
      'priority_support',
    ],
  },
  family: {
    subjects: 9, // All subjects
    quizzesPerDay: -1,
    features: [
      'chapter_essence',
      'basic_quiz',
      'parent_dashboard',
      'progress_tracking',
      'exam_calendar',
      'ai_video_explainer',
      'mock_exam',
      'group_study',
      'ai_chat',
      'ocr_upload',
      'audio_narration',
      'priority_support',
      'multi_child', // Up to 3 children
      'multi_parent',
      'detailed_analytics',
      'whatsapp_alerts', // Coming soon
    ],
  },
};

export const FEATURE_NAMES = {
  chapter_essence: 'Chapter Essence',
  basic_quiz: 'Quizzes',
  parent_dashboard: 'Parent Dashboard',
  progress_tracking: 'Progress Tracking',
  exam_calendar: 'Exam Calendar',
  ai_video_explainer: 'AI Video Explainers',
  mock_exam: 'Mock Exams',
  group_study: 'Group Study',
  ai_chat: 'AI Chapter Chat',
  ocr_upload: 'Textbook Scanner',
  audio_narration: 'Audio Narration',
  priority_support: 'Priority Support',
  multi_child: 'Multiple Children',
  multi_parent: 'Multi-Parent Access',
  detailed_analytics: 'Detailed Analytics',
  whatsapp_alerts: 'WhatsApp Alerts',
};

export const FEATURE_MIN_PLAN = {
  chapter_essence: 'basic',
  basic_quiz: 'basic',
  parent_dashboard: 'basic',
  progress_tracking: 'basic',
  exam_calendar: 'basic',
  ai_video_explainer: 'pro',
  mock_exam: 'pro',
  group_study: 'pro',
  ai_chat: 'pro',
  ocr_upload: 'pro',
  audio_narration: 'pro',
  priority_support: 'pro',
  multi_child: 'family',
  multi_parent: 'family',
  detailed_analytics: 'family',
  whatsapp_alerts: 'family',
};

// All available subjects
export const ALL_SUBJECTS = ['Math', 'Science', 'English', 'Social Science', 'Hindi', 'EVS', 'Physics', 'Chemistry', 'Biology'];

// Check if user has access to a feature
export const hasFeatureAccess = (subscription, feature) => {
  const plan = subscription || 'free';
  const planFeatures = SUBSCRIPTION_FEATURES[plan]?.features || [];
  return planFeatures.includes(feature);
};

// Check if user has access to a subject
export const hasSubjectAccess = (subscription, subjectIndex, selectedSubjects = []) => {
  const plan = subscription || 'free';
  const maxSubjects = SUBSCRIPTION_FEATURES[plan]?.subjects || 0;
  
  // If unlimited subjects (pro/family), allow all
  if (maxSubjects >= 9) return true;
  
  // For basic, check if subject is in selected subjects
  if (selectedSubjects.length > 0) {
    return selectedSubjects.includes(ALL_SUBJECTS[subjectIndex]);
  }
  
  // Default: allow first N subjects
  return subjectIndex < maxSubjects;
};

// Get the minimum plan required for a feature
export const getMinPlanForFeature = (feature) => {
  return FEATURE_MIN_PLAN[feature] || 'pro';
};

// Get upgrade message for a locked feature
export const getUpgradeMessage = (feature) => {
  const minPlan = getMinPlanForFeature(feature);
  const featureName = FEATURE_NAMES[feature] || feature;
  
  if (minPlan === 'pro') {
    return `Upgrade to Pro (₹299/month) to unlock ${featureName}`;
  } else if (minPlan === 'family') {
    return `Upgrade to Family (₹499/month) to unlock ${featureName}`;
  }
  return `Subscribe to unlock ${featureName}`;
};

// Check remaining quizzes for the day
export const canTakeQuiz = (subscription, quizzesTakenToday) => {
  const plan = subscription || 'free';
  const limit = SUBSCRIPTION_FEATURES[plan]?.quizzesPerDay || 0;
  
  if (limit === -1) return true; // Unlimited
  return quizzesTakenToday < limit;
};

// Get quiz limit info
export const getQuizLimitInfo = (subscription, quizzesTakenToday) => {
  const plan = subscription || 'free';
  const limit = SUBSCRIPTION_FEATURES[plan]?.quizzesPerDay || 0;
  
  if (limit === -1) return { unlimited: true, remaining: -1, limit: -1 };
  return {
    unlimited: false,
    remaining: Math.max(0, limit - quizzesTakenToday),
    limit,
    used: quizzesTakenToday,
  };
};
